import {applyGridCommands, type GridCommand} from "./commands";
import {createGridDocument, validateGridDocument, type GridDocument} from "./document";
export type WorkspaceState = {document: GridDocument; selectedId: string | null; dirty: boolean};
export const visibleGridDocument = (state: WorkspaceState) => state.document;
export type WorkspaceAction = {type: "select"; instanceId: string} | {type: "commands"; commands: GridCommand[]; selectId?: string};
export function createWorkspace(): WorkspaceState {
  return {document: createGridDocument(), selectedId: null, dirty: false};
}
export function openGridWorkspace(document: GridDocument): WorkspaceState {
  validateGridDocument(document);
  return {document, selectedId: document.banners[0]?.instanceId ?? null, dirty: false};
}
// A save can complete after more edits. Only its submitted document may become clean.
export function completeGridSave(state: WorkspaceState, submitted: GridDocument, saved: GridDocument): WorkspaceState {
  if (state.document !== submitted) return state;
  validateGridDocument(saved);
  return {...state, document: saved, dirty: false};
}
export function updateWorkspace(state: WorkspaceState, action: WorkspaceAction): WorkspaceState {
  if (action.type === "select") {
    if (!visibleGridDocument(state).banners.some((banner) => banner.instanceId === action.instanceId)) throw new Error(`Unknown banner: ${action.instanceId}`);
    return state.selectedId === action.instanceId ? state : {...state, selectedId: action.instanceId};
  }
  if (!action.commands.length) return state;
  const document = applyGridCommands(state.document, action.commands);
  let selectedId = action.selectId ?? state.selectedId;
  if (selectedId !== null && !document.banners.some((banner) => banner.instanceId === selectedId)) {
    if (action.selectId) throw new Error(`Unknown selection: ${action.selectId}`);
    const oldIndex = state.document.banners.findIndex((banner) => banner.instanceId === selectedId);
    selectedId = document.banners[Math.min(oldIndex, document.banners.length - 1)]?.instanceId ?? null;
  }
  if (selectedId === null && document.banners.length) selectedId = document.banners[0].instanceId;
  return {document, selectedId, dirty: true};
}
export function reorderedIds(document: GridDocument, activeId: string, overId: string): string[] {
  const ids = document.banners.map((banner) => banner.instanceId);
  const from = ids.indexOf(activeId), to = ids.indexOf(overId);
  if (from < 0 || to < 0) throw new Error("Unknown reorder target");
  ids.splice(to, 0, ...ids.splice(from, 1));
  return ids;
}
